const initSlideshowStaticArrows = (root = document) => {
  const sections = root.querySelectorAll('.slideshow-section');
  if (!sections.length) return;

  const setArrowEnabled = (button, enabled) => {
    if (!button) return;
    const isDisabled = !enabled;
    button.disabled = isDisabled;
    button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
    button.classList.toggle('is-disabled', isDisabled);
  };

  sections.forEach((section) => {
    const staticControls = section.querySelector('.slideshow-static-controls');
    if (!staticControls) return;
    if (staticControls.getAttribute('data-static-arrows-init') === 'true') return;

    const slideshow = section.querySelector('slideshow-component');
    const previousButton = staticControls.querySelector('.slideshow-control--previous');
    const nextButton = staticControls.querySelector('.slideshow-control--next');
    if (!slideshow || (!previousButton && !nextButton)) return;

    staticControls.setAttribute('data-static-arrows-init', 'true');

    const syncArrowState = () => {
      const isInfinite = slideshow.hasAttribute('infinite');
      if (isInfinite) {
        setArrowEnabled(previousButton, true);
        setArrowEnabled(nextButton, true);
        return;
      }

      const slides = Array.from(slideshow.querySelectorAll('slideshow-slide:not([hidden])'));
      if (!slides.length) return;

      const activeIndex = slides.findIndex((slide) => slide.getAttribute('aria-hidden') !== 'true');
      if (activeIndex < 0) return;

      const atStart = activeIndex === 0;
      const atEnd = activeIndex === slides.length - 1;

      setArrowEnabled(previousButton, !atStart);
      setArrowEnabled(nextButton, !atEnd);
    };

    const observer = new MutationObserver(syncArrowState);
    slideshow.querySelectorAll('slideshow-slide').forEach((slide) => {
      observer.observe(slide, { attributes: true, attributeFilter: ['aria-hidden', 'hidden'] });
    });

    syncArrowState();
  });
};

const initSlideshowVideoControls = (root = document) => {
  initSlideshowStaticArrows(root);

  const videoContainers = root.querySelectorAll('[data-slideshow-video]');
  if (!videoContainers.length) return;

  const sharedState = window.__slideshowVideoControlState || {
    userPaused: null,
    muted: null,
  };
  window.__slideshowVideoControlState = sharedState;

  const syncCallbacks = window.__slideshowVideoSyncCallbacks || new Set();
  window.__slideshowVideoSyncCallbacks = syncCallbacks;

  const syncAllStates = () => {
    syncCallbacks.forEach((sync) => {
      try {
        sync();
      } catch {
        // no-op
      }
    });
  };

  const updateToggleButton = (button, isPlaying) => {
    if (!button) return;
    button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    const playIcon = button.querySelector('[data-video-icon="play"]');
    const pauseIcon = button.querySelector('[data-video-icon="pause"]');
    if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
    if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    const label = button.querySelector('[data-video-label]');
    if (label) label.textContent = isPlaying ? 'Pause video' : 'Play video';
  };

  const updateMuteButton = (button, isMuted) => {
    if (!button) return;
    button.setAttribute('aria-pressed', isMuted ? 'true' : 'false');
    const muteLabel = button.querySelector('[data-video-mute-label]');
    const unmuteLabel = button.querySelector('[data-video-unmute-label]');
    if (muteLabel) muteLabel.classList.toggle('hidden', !isMuted);
    if (unmuteLabel) unmuteLabel.classList.toggle('hidden', isMuted);
  };

  const pauseVideo = (container, video) => {
    if (video && !video.paused) {
      video.pause();
    }
    container.dataset.videoPlaying = 'false';
  };

  const playVideo = async (container, video) => {
    if (!video) return;
    try {
      await video.play();
      container.dataset.videoPlaying = 'true';
      container.dataset.videoStarted = 'true';
    } catch {
      container.dataset.videoPlaying = 'false';
    }
  };

  const applySharedStateToAllVideos = () => {
    const allVideoContainers = document.querySelectorAll('[data-slideshow-video]');
    allVideoContainers.forEach((videoContainer) => {
      const video = videoContainer.querySelector('video');
      if (!video) return;

      const slide = videoContainer.closest('slideshow-slide');
      const isHidden = slide?.getAttribute('aria-hidden') === 'true';
      const autoplay = videoContainer.dataset.videoAutoplay === 'true';

      if (sharedState.muted !== null) {
        video.muted = sharedState.muted;
      }

      if (sharedState.userPaused === true) {
        videoContainer.dataset.userPaused = 'true';
        pauseVideo(videoContainer, video);
        return;
      }

      if (sharedState.userPaused === false) {
        videoContainer.dataset.userPaused = 'false';
        if (!isHidden && autoplay) {
          playVideo(videoContainer, video);
        }
      }
    });
  };

  // Returns the video context for the currently active slide in a slideshow section,
  // or the first available video if slide-based lookup is not possible.
  const getActiveVideoContext = (slideshowSection) => {
    const activeSlide = slideshowSection.querySelector('slideshow-slide:not([aria-hidden="true"])');    
    if (!activeSlide) return null;
    const container = activeSlide.querySelector('[data-slideshow-video]');
    if (!container) return null;
    const video = container.querySelector('video');
    return video ? { container, video } : null;
  };

  // Initialise static controls once per slideshow section. Returns a syncState function.
  const initStaticControls = (slideshowSection) => {
    const staticEl = slideshowSection.querySelector('[data-static-video-controls]');
    if (!staticEl) return null;
    if (staticEl.dataset.staticControlsInit === 'true') {
      // Already initialised — return the stored syncState reference
      if (staticEl._syncState) syncCallbacks.add(staticEl._syncState);
      return staticEl._syncState ?? null;
    }
    staticEl.dataset.staticControlsInit = 'true';

    const toggleButton = staticEl.querySelector('[data-video-toggle]');
    const muteButton = staticEl.querySelector('[data-video-mute]');

    const syncState = () => {
      // Determine whether the currently active slide contains a video.
      const activeSlide = slideshowSection.querySelector('slideshow-slide:not([aria-hidden="true"])');
      
      if (!activeSlide) return;

      const hasVideo = !!(activeSlide && activeSlide.querySelector('[data-slideshow-video]'));

      // If the active slide has no video, hide static video controls and
      // do not try to sync play/pause state. Arrows remain unaffected.
      if (!hasVideo) {
        if (toggleButton) {
          toggleButton.style.display = 'none';
          toggleButton.setAttribute('aria-hidden', 'true');
        }
        if (muteButton) {
          muteButton.style.display = 'none';
          muteButton.setAttribute('aria-hidden', 'true');
        }
        return;
      }

      // Ensure controls are visible when a video is present.
      if (toggleButton) {
        toggleButton.style.display = '';
        toggleButton.removeAttribute('aria-hidden');
      }
      if (muteButton) {
        muteButton.style.display = '';
        muteButton.removeAttribute('aria-hidden');
      }

      const ctx = getActiveVideoContext(slideshowSection);
      if (!ctx) return;
      const isPlaying = !ctx.video.paused && !ctx.video.ended;
      ctx.container.dataset.videoPlaying = isPlaying ? 'true' : 'false';
      updateToggleButton(toggleButton, isPlaying);
      updateMuteButton(muteButton, ctx.video.muted);
    };

    toggleButton?.addEventListener('click', () => {
      const ctx = getActiveVideoContext(slideshowSection);
      if (!ctx) return;
      if (ctx.video.paused) {
        sharedState.userPaused = false;
        ctx.container.dataset.userPaused = 'false';
        playVideo(ctx.container, ctx.video);
      } else {
        sharedState.userPaused = true;
        ctx.container.dataset.userPaused = 'true';
        pauseVideo(ctx.container, ctx.video);
      }
      applySharedStateToAllVideos();
      syncAllStates();
      syncState();
    });

    muteButton?.addEventListener('click', () => {
      const ctx = getActiveVideoContext(slideshowSection);
      if (!ctx) return;
      ctx.video.muted = !ctx.video.muted;
      sharedState.muted = ctx.video.muted;
      applySharedStateToAllVideos();
      syncAllStates();
      syncState();
    });

    // Store reference for subsequent calls
    staticEl._syncState = syncState;
    syncCallbacks.add(syncState);
    return syncState;
  };

  videoContainers.forEach((container) => {
    if (container.dataset.videoControlsInit === 'true') return;
    container.dataset.videoControlsInit = 'true';

    const video = container.querySelector('video');
    if (!video) return;

    const slide = container.closest('slideshow-slide');
    const slideshowSection = container.closest('.slideshow-section');
    const autoplay = container.dataset.videoAutoplay === 'true';

    // Use static controls if available; fall back to inline controls (non-hero contexts)
    let toggleButton, muteButton, syncState;
    if (slideshowSection) {
      const syncStaticState = initStaticControls(slideshowSection);
      syncState = syncStaticState ?? (() => {});
    } else {
      const controlsRoot = slide || container;
      toggleButton = controlsRoot.querySelector('[data-video-toggle]');
      muteButton = controlsRoot.querySelector('[data-video-mute]');
      syncState = () => {
        const isPlaying = !video.paused && !video.ended;
        container.dataset.videoPlaying = isPlaying ? 'true' : 'false';
        updateToggleButton(toggleButton, isPlaying);
        updateMuteButton(muteButton, video.muted);
      };
      toggleButton?.addEventListener('click', () => {
        if (video.paused) {
          sharedState.userPaused = false;
          container.dataset.userPaused = 'false';
          playVideo(container, video);
        } else {
          sharedState.userPaused = true;
          container.dataset.userPaused = 'true';
          pauseVideo(container, video);
        }
        applySharedStateToAllVideos();
        syncAllStates();
        syncState();
      });
      muteButton?.addEventListener('click', () => {
        video.muted = !video.muted;
        sharedState.muted = video.muted;
        applySharedStateToAllVideos();
        syncAllStates();
        syncState();
      });
    }

    syncCallbacks.add(syncState);

    video.addEventListener('play', syncState);
    video.addEventListener('pause', syncState);
    video.addEventListener('volumechange', syncState);

    if (slide) {
      const observer = new MutationObserver(() => {
        const isHidden = slide.getAttribute('aria-hidden') === 'true';
        const userPaused = sharedState.userPaused ?? (container.dataset.userPaused === 'true');
        if (isHidden) {
          pauseVideo(container, video);
        } else if (autoplay && userPaused !== true) {
          playVideo(container, video);
        }
        syncState();
      });
      observer.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
    }

    if (sharedState.muted !== null) {
      video.muted = sharedState.muted;
    }

    if (sharedState.userPaused === true) {
      container.dataset.userPaused = 'true';
      pauseVideo(container, video);
      syncState();
    } else if (
      (sharedState.userPaused === false || sharedState.userPaused === null)
      && autoplay
      && (!slide || slide.getAttribute('aria-hidden') !== 'true')
    ) {
      if (sharedState.userPaused === false) {
        container.dataset.userPaused = 'false';
      }
      playVideo(container, video).finally(syncState);
    } else {
      syncState();
    }
  });

  applySharedStateToAllVideos();
  syncAllStates();
};

initSlideshowVideoControls();

document.addEventListener('shopify:section:load', (event) => {
  initSlideshowVideoControls();
});
